import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Mail, Phone, ShoppingBag, Search } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-customers">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900">{customers.length} Customers</h2>
              <p className="text-sm text-stone-500">All registered customers and their details</p>
            </div>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-customers"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-stone-500">
              No customers found
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full admin-table">
              <thead className="bg-stone-50">
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th className="text-right">Orders</th>
                  <th className="text-right">Total Spent</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-green-900 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {customer.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-stone-900" data-testid="customer-name">{customer.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-stone-600">
                        <Mail className="w-4 h-4 text-stone-400" />
                        <a href={`mailto:${customer.email}`} className="hover:text-green-700" data-testid="customer-email">
                          {customer.email}
                        </a>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-stone-600">
                        <Phone className="w-4 h-4 text-stone-400" />
                        <span data-testid="customer-phone">{customer.phone || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <ShoppingBag className="w-4 h-4 text-stone-400" />
                        <span data-testid="customer-orders">{customer.order_count}</span>
                      </div>
                    </td>
                    <td className="text-right font-semibold text-green-700" data-testid="customer-spent">
                      R{customer.total_spent.toFixed(2)}
                    </td>
                    <td className="text-stone-500 text-sm">
                      {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCustomers;
